import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useAuth } from '@/contexts';
import { fetchBuscaApuracaoDetalhada } from '@/services/ApuracaoDetalhada/api/BuscaApuracaoDetalhadaApi';
import { fetchTotaisImpostosTradicionais } from '../api/TotaisImpostosTradicionaisApi';
import type { FiltrosGestaoImpostos, HistoricoMensalItem } from '../types';

const NOMES_MES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

interface MesReferencia {
  mesReferencia: string;
  label: string;
  dataInicio: string;
  dataFim: string;
}

/** Gera os últimos `quantidade` meses fechados, do mais recente ao mais antigo, a partir do fim do período filtrado no header. */
function gerarUltimosMeses(
  dataReferencia: string | null | undefined,
  quantidade: number
): MesReferencia[] {
  const base = dataReferencia ? new Date(dataReferencia) : new Date();
  const meses: MesReferencia[] = [];

  for (let i = 0; i < quantidade; i++) {
    const ano = base.getFullYear();
    const mes = base.getMonth() - i; // pode ficar negativo; Date normaliza o ano
    const primeiroDia = new Date(ano, mes, 1);
    const ultimoDia = new Date(ano, mes + 1, 0);
    const mesNum = String(primeiroDia.getMonth() + 1).padStart(2, '0');

    meses.push({
      mesReferencia: `${primeiroDia.getFullYear()}-${mesNum}`,
      label: `${NOMES_MES[primeiroDia.getMonth()]}/${primeiroDia.getFullYear()}`,
      dataInicio: `${primeiroDia.getFullYear()}-${mesNum}-01`,
      dataFim: `${ultimoDia.getFullYear()}-${mesNum}-${String(ultimoDia.getDate()).padStart(2, '0')}`,
    });
  }

  return meses;
}

/**
 * Histórico dos últimos 12 meses fechados a partir do período filtrado no
 * header. Cada mês dispara sua própria chamada de Apuração Detalhada
 * (IBS/CBS/vendas, já agregado) e de totais tradicionais (ICMS/IPI/PIS/COFINS,
 * paginado) — 12 buscas em paralelo, não sequenciais.
 *
 * Meses sem nenhum documento não entram na lista (requisito do chamado: "sem
 * preencher meses inexistentes com valores zerados ou fictícios").
 */
export function useHistoricoMensal(filtros: FiltrosGestaoImpostos, meses = 12, enabled = true) {
  const { user } = useAuth();
  const cnpjGestor = user?.cnpj;

  const listaMeses = useMemo(
    () => gerarUltimosMeses(filtros.dataFim, meses),
    [filtros.dataFim, meses]
  );

  const queriesApuracao = useQueries({
    queries: listaMeses.map(mes => ({
      queryKey: [
        'gestao-impostos-historico-apuracao',
        cnpjGestor,
        filtros.empresaId,
        mes.mesReferencia,
      ],
      queryFn: () =>
        fetchBuscaApuracaoDetalhada({
          empresaId: filtros.empresaId,
          dataInicio: mes.dataInicio,
          dataFim: mes.dataFim,
        }),
      staleTime: 5 * 60 * 1000,
      enabled: enabled && !!cnpjGestor && !!filtros.empresaId,
    })),
  });

  const queriesTradicionais = useQueries({
    queries: listaMeses.map(mes => ({
      queryKey: [
        'gestao-impostos-historico-tradicionais',
        cnpjGestor,
        filtros.empresaId,
        mes.mesReferencia,
      ],
      queryFn: () =>
        fetchTotaisImpostosTradicionais({
          empresaId: filtros.empresaId,
          dataInicio: mes.dataInicio,
          dataFim: mes.dataFim,
        }),
      staleTime: 5 * 60 * 1000,
      enabled: enabled && !!cnpjGestor && !!filtros.empresaId,
    })),
  });

  const isLoading =
    queriesApuracao.some(q => q.isLoading) || queriesTradicionais.some(q => q.isLoading);
  const isError = queriesApuracao.some(q => q.isError) || queriesTradicionais.some(q => q.isError);

  const historico = useMemo<HistoricoMensalItem[]>(() => {
    const itens: HistoricoMensalItem[] = [];

    listaMeses.forEach((mes, idx) => {
      const apuracao = queriesApuracao[idx]?.data?.data?.totais;
      const tradicionais = queriesTradicionais[idx]?.data;
      if (!apuracao || !tradicionais) return;

      const totalVendas = apuracao.TotalValorOperacao;
      const temDados =
        totalVendas > 0 ||
        tradicionais.icms > 0 ||
        tradicionais.ipi > 0 ||
        tradicionais.pis > 0 ||
        tradicionais.cofins > 0 ||
        apuracao.TotalValorIBS > 0 ||
        apuracao.TotalValorCBS > 0;
      if (!temDados) return; // mês sem movimento não entra no histórico

      const totalImpostos =
        tradicionais.icms + tradicionais.ipi + tradicionais.pis + tradicionais.cofins;

      itens.push({
        mesReferencia: mes.mesReferencia,
        label: mes.label,
        totalVendas,
        icms: tradicionais.icms,
        pis: tradicionais.pis,
        cofins: tradicionais.cofins,
        ipi: tradicionais.ipi,
        totalImpostos,
        percentualSobreVendas: totalVendas ? totalImpostos / totalVendas : 0,
      });
    });

    // mais recente primeiro
    return itens.sort((a, b) => (a.mesReferencia < b.mesReferencia ? 1 : -1));
  }, [listaMeses, queriesApuracao, queriesTradicionais]);

  return { historico, isLoading, isError };
}
